import React, { useCallback, useEffect, useState } from 'react';
import Navigation from '../components/Navigation/Navigation';
import apiClient from '../utils/apiClient';
import './StaffPage.css';

export interface MinistryPerson {
  id: number; name: string; bulletin_name_en: string; bulletin_name_es: string; revision: number;
}
interface Assignment { date: string; title: string; person_id: number | null; revision: number }
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const endOfWindow = () => { const d = new Date(today() + 'T12:00:00Z'); d.setUTCMonth(d.getUTCMonth() + 3); return d.toISOString().slice(0,10); };
const showDate = (date: string) => new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
const detail = (error: any) => typeof error?.response?.data?.error === 'string' ? error.response.data.error : 'Could not complete this change. Please try again.';
const emptyPerson = { name: '', bulletin_name_en: '', bulletin_name_es: '' };

const StaffPage: React.FC = () => {
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(endOfWindow);
  const [rows, setRows] = useState<Assignment[]>([]);
  const [people, setPeople] = useState<MinistryPerson[]>([]);
  const [changes, setChanges] = useState<Record<string, number | null>>({});
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [parish, setParish] = useState('St. Luke’s / San Lucas');
  const [editing, setEditing] = useState<MinistryPerson | null>(null);
  const [person, setPerson] = useState(emptyPerson);
  const dirty = Object.keys(changes).length > 0;

  const load = useCallback(async () => {
    setBusy(true); setError('');
    try {
      const [schedule, roster] = await Promise.all([
        apiClient.get('/staff/preaching', { params: { from, to } }), apiClient.get('/staff/people'),
      ]);
      setRows(schedule.data.assignments); setParish(schedule.data.parish.name);
      setPeople(roster.data.people); setChanges({}); setLoaded(true);
    } catch (err) { setError(detail(err)); }
    finally { setBusy(false); }
  }, [from, to]);
  // Date inputs are applied explicitly, so editing them cannot discard work.
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      const assignments = rows.filter(row => Object.prototype.hasOwnProperty.call(changes,row.date))
        .map(row => ({ date: row.date, revision: row.revision, person_id: changes[row.date] }));
      await apiClient.put('/staff/preaching', { assignments });
      setNotice(`Saved ${assignments.length} ${assignments.length === 1 ? 'date' : 'dates'}. All three bulletins will use this schedule.`);
      await load();
    } catch (err) { setError(detail(err)); }
    finally { setBusy(false); }
  };
  const savePerson = async (event: React.FormEvent) => {
    event.preventDefault(); setBusy(true); setError(''); setNotice('');
    try {
      const body = Object.fromEntries(Object.entries(person).map(([key,value]) => [key,value.trim()]));
      const response = editing
        ? await apiClient.put(`/staff/people/${editing.id}`, { ...body, revision: editing.revision })
        : await apiClient.post('/staff/people', body);
      setPeople(current => [...current.filter(p => p.id !== response.data.id), response.data].sort((a,b) => a.name.localeCompare(b.name)));
      setNotice(editing ? 'Preacher details updated.' : 'Preacher added. You can now select them in the schedule.');
      setEditing(null); setPerson(emptyPerson);
    } catch (err) { setError(detail(err)); }
    finally { setBusy(false); }
  };

  return <><Navigation /><main className="staff-page">
    <header className="staff-heading"><div><p className="staff-eyebrow">Staff · {parish}</p>
      <h1>Preaching schedule</h1><p>One preacher for each date, shared by the English and Spanish services.</p></div>
      <a className="btn btn-outline-primary" href="#preachers">Manage preachers</a></header>
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {notice && <div className="alert alert-success" role="status">{notice}</div>}
    <section className="staff-card" aria-label="Preaching schedule">
      <form className="staff-range" onSubmit={event => { event.preventDefault(); setNotice(''); load(); }}>
        <label>From<input type="date" className="form-control" value={from} disabled={busy || dirty} onChange={e => setFrom(e.target.value)} required /></label>
        <label>Through<input type="date" className="form-control" value={to} disabled={busy || dirty} onChange={e => setTo(e.target.value)} required min={from} /></label>
        <button className="btn btn-outline-primary" disabled={busy || dirty}>Show dates / Reload</button>
      </form>
      <div className="staff-table-wrap"><table className="table staff-table"><thead><tr><th scope="col">Date</th><th scope="col">Liturgical day</th><th scope="col">Preacher</th></tr></thead>
        <tbody>{rows.map(row => {
          const changed = Object.prototype.hasOwnProperty.call(changes,row.date);
          const value = changed ? changes[row.date] : row.person_id;
          return <tr key={row.date} className={changed ? 'staff-changed' : ''}>
            <th scope="row"><time dateTime={row.date}>{showDate(row.date)}</time>{changed && <small className="staff-unsaved">Unsaved change</small>}</th>
            <td>{row.title}</td><td><select className={`form-select ${value === null ? 'staff-unassigned' : ''}`} aria-label={`Preacher for ${showDate(row.date)}`} value={value ?? ''} disabled={busy}
              onChange={e => { const id = e.target.value ? Number(e.target.value) : null; setNotice(''); setChanges(current => { const next = { ...current }; if (id === row.person_id) delete next[row.date]; else next[row.date] = id; return next; }); }}>
              <option value="">Not assigned</option>{people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></td></tr>;
        })}</tbody></table></div>
      {!loaded && <p className="p-3">{busy ? 'Loading schedule…' : 'Use Reload to load the schedule.'}</p>}
      {loaded && !rows.length && <p className="p-3">No Sundays or planned services in this range.</p>}
      <div className="staff-save"><span aria-live="polite">{dirty ? `${Object.keys(changes).length} unsaved ${Object.keys(changes).length === 1 ? 'date' : 'dates'}` : 'Choose preachers above, then save.'}</span>
        <button className="btn btn-outline-secondary" disabled={!dirty || busy} onClick={() => { setChanges({}); setNotice(''); }}>Discard changes</button>
        <button className="btn btn-primary" disabled={!dirty || busy} onClick={save}>{busy ? 'Please wait…' : 'Save schedule'}</button></div>
    </section>
    <section id="preachers" className="staff-card staff-people"><h2>Preachers</h2><p>Save the names exactly as they should appear in each bulletin. Preachers do not need a website account.</p>
      <div className="staff-roster">{people.map(p => <article key={p.id}><strong>{p.name}</strong><span>{p.bulletin_name_en}</span><span lang="es">{p.bulletin_name_es}</span>
        <button className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => { setEditing(p); setPerson({ name:p.name, bulletin_name_en:p.bulletin_name_en, bulletin_name_es:p.bulletin_name_es }); }}>Edit {p.name}</button></article>)}</div>
      <form onSubmit={savePerson}><h3>{editing ? `Edit ${editing.name}` : 'Add a preacher'}</h3><div className="staff-person-fields">
        <label>Name<input required maxLength={120} className="form-control" value={person.name} disabled={busy} onChange={e => setPerson({ ...person,name:e.target.value })} /></label>
        <label>English bulletin name<input required maxLength={200} className="form-control" value={person.bulletin_name_en} disabled={busy} placeholder="The Rev. …" onChange={e => setPerson({ ...person,bulletin_name_en:e.target.value })} /></label>
        <label>Spanish bulletin name<input required maxLength={200} className="form-control" value={person.bulletin_name_es} disabled={busy} placeholder="El Rvdo. …" onChange={e => setPerson({ ...person,bulletin_name_es:e.target.value })} /></label>
      </div><button className="btn btn-primary" disabled={busy}>{editing ? 'Save preacher details' : 'Add preacher'}</button>{editing && <button type="button" className="btn btn-link" onClick={() => { setEditing(null); setPerson(emptyPerson); }}>Cancel</button>}</form>
    </section>
  </main></>;
};
export default StaffPage;
